import React, { useState } from 'react';
import { Sparkles, TrendingUp, Target, Zap, Share2, Download, Check, Copy, X, Calendar, Link, BarChart3 } from 'lucide-react';
import { useSavedListings } from '../hooks/useSavedListings';
import { useShareAnalysis } from '../hooks/useShareAnalysis';
import ShareAnalysisModal from './ShareAnalysisModal';

/**
 * Overall Verdict Card - Primary assessment displayed at top of analysis
 * Shows score, category, key insight, and quick actions
 * 
 * @param {Object} props
 * @param {number} props.score - Overall score (0-100)
 * @param {string} props.category - Score category (e.g., "Good Investment")
 * @param {string} props.keyInsight - Main takeaway for the agent
 * @param {Object} props.result - Full analysis result for saving
 */
function OverallVerdictCard({ score, category, keyInsight, result }) {
    const { saveListing, isListingSaved, loading } = useSavedListings();
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);

    const handleSave = async () => {
        if (!result) return;

        try {
            setSaveError(null);
            await saveListing(
                result.url || window.location.href,
                result.propertyData,
                result.analysis,
                '', // notes
                [] // tags
            );
            setSaved(true);
            setTimeout(() => setSaved(false), 3000); // Reset after 3s
        } catch (error) {
            console.error('Save error:', error);
            setSaveError('Failed to save. Please try again.');
        }
    };
    // Determine color scheme based on score
    const getColorScheme = (score) => {
        if (score >= 80) return {
            gradient: 'from-emerald-500 to-emerald-600',
            bg: 'from-emerald-50 via-white to-emerald-50',
            text: 'text-emerald-600',
            badge: 'bg-emerald-100 text-emerald-700'
        };
        if (score >= 70) return {
            gradient: 'from-primary-500 to-primary-600',
            bg: 'from-primary-50 via-white to-primary-50',
            text: 'text-primary-600',
            badge: 'bg-primary-100 text-primary-700'
        };
        if (score >= 60) return {
            gradient: 'from-blue-500 to-blue-600',
            bg: 'from-blue-50 via-white to-blue-50',
            text: 'text-blue-600',
            badge: 'bg-blue-100 text-blue-700'
        };
        if (score >= 50) return {
            gradient: 'from-yellow-500 to-yellow-600',
            bg: 'from-yellow-50 via-white to-yellow-50',
            text: 'text-yellow-600',
            badge: 'bg-yellow-100 text-yellow-700'
        };
        return {
            gradient: 'from-orange-500 to-orange-600',
            bg: 'from-orange-50 via-white to-orange-50',
            text: 'text-orange-600',
            badge: 'bg-orange-100 text-orange-700'
        };
    };

    const colors = getColorScheme(score);

    return (
        <>
            <div className={`bg-gradient-to-br ${colors.bg} rounded-2xl shadow-lg border-2 border-slate-200 p-8 mb-8 animate-in fade-in slide-in-from-top-2 duration-500`}>
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Left: Score & Verdict */}
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`p-3 bg-gradient-to-br ${colors.gradient} rounded-xl shadow-md`}>
                                <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900">Overall Verdict</h3>
                        </div>

                        <div className="flex items-baseline gap-3 mb-4">
                            <div className={`text-7xl font-bold ${colors.text}`}>{score}</div>
                            <div className="text-3xl text-slate-400">/100</div>
                        </div>

                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-4 ${colors.badge}`}>
                            <TrendingUp className="w-5 h-5" />
                            <span className="font-semibold text-lg">{category}</span>
                        </div>

                        <p className="text-sm text-slate-700 leading-relaxed mt-4 bg-white/50 p-4 rounded-lg border border-slate-200">
                            {keyInsight}
                        </p>
                    </div>

                    {/* Right: Quick Actions */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-slate-200">
                        <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Quick Actions
                        </h4>
                        <div className="space-y-3">
                            <button
                                onClick={handleSave}
                                disabled={saved}
                                aria-label={saved ? "Property saved to portfolio" : "Save property to portfolio"}
                                aria-pressed={saved}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium min-h-[44px] touch-manipulation rounded-lg transition-all group ${saved ? 'bg-green-50 text-green-700 border border-green-200' : `${colors.bg} ${colors.text} hover:opacity-90 border border-transparent hover:shadow-md`
                                    }`}
                            >
                                {saved ? (
                                    <>
                                        <Check className="w-4 h-4 text-green-600" aria-hidden="true" />
                                        <span>Saved!</span>
                                    </>
                                ) : (
                                    <>
                                        <Target className={`w-4 h-4 ${colors.text} group-hover:scale-110 transition-transform`} aria-hidden="true" />
                                        <span>Save to Portfolio</span>
                                    </>
                                )}
                            </button>
                            <button
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-all hover:shadow-sm group min-h-[44px] touch-manipulation"
                                aria-label="Compare with similar properties"
                                disabled
                            >
                                <Zap className={`w-4 h-4 ${colors.text} group-hover:scale-110 transition-transform`} aria-hidden="true" />
                                <span>Compare Similar</span>
                                <span className="ml-auto text-xs text-slate-400" aria-label="Feature coming soon">Coming soon</span>
                            </button>
                            <button
                                onClick={() => setShowShareModal(true)}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 border border-emerald-600 rounded-lg transition-all hover:shadow-md group min-h-[44px] touch-manipulation"
                                aria-label="Share property analysis with client"
                            >
                                <Share2 className="w-4 h-4 group-hover:scale-110 transition-transform" aria-hidden="true" />
                                <span>Share with Client</span>
                            </button>
                            <button
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-all hover:shadow-sm group min-h-[44px] touch-manipulation"
                                aria-label="Download property analysis report as PDF"
                                disabled
                            >
                                <Download className={`w-4 h-4 ${colors.text} group-hover:scale-110 transition-transform`} aria-hidden="true" />
                                <span>Download Report</span>
                                <span className="ml-auto text-xs text-slate-400" aria-label="Feature coming soon">Coming soon</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Share Analysis Modal */}
            <ShareAnalysisModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                propertyData={result?.propertyData}
                analysis={result?.analysis}
            />
        </>
    );
}

export default OverallVerdictCard;
